// auditpm-v6
// v5fix3
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase, AUDIT_TYPES, resolveScope } from '../lib/supabase'
import { calcBizSAFEMandays, classifyComplexity, resolveCT17Industry } from '../lib/ct17'
import { FirmHeader } from '../components/PublicComponents'
import { DEFAULT_QUESTIONNAIRES as DEFAULT_QUESTIONS } from '../lib/questionnaireDefaults'
import { emailHtml } from '../lib/emailHtml'
import { isIA } from '../lib/service'
import { oneLine, isAddressField } from '../lib/oneLine'
import { toast, failed } from '../lib/toast'

function Field({ q, value, onChange, invalid }) {
  // Address: always a one-line box (line breaks become ", "), whatever the template says.
  if (isAddressField(q)) { const set = onChange; q = { ...q, type: 'text' }; onChange = v => set(oneLine(v)); value = oneLine(value) }
  const s ={ width: '100%', padding: '9px 11px', border: `1px solid ${invalid ? '#c0392b' : 'rgba(0,0,0,0.22)'}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: invalid ? '#fff5f5' : '#fff', color: '#006363' }
  if (q.type === 'textarea') return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>{q.label}{q.required && <span style={{ color: '#c0392b', marginLeft: 3 }}>*</span>}</label>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...s, resize: 'vertical', minHeight: 72 }} />
    </div>
  )
  if (q.type === 'select') return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>{q.label}{q.required && <span style={{ color: '#c0392b', marginLeft: 3 }}>*</span>}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)} style={s}>
        <option value="">Select…</option>
        {(q.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
  if (q.type === 'multicheck') return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 6 }}>{q.label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(q.options || []).map(opt => {
          const checked = (value || []).includes(opt)
          return (
            <label key={opt} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, lineHeight: 1.5 }}>
              <input type="checkbox" checked={checked}
                onChange={() => {
                  const arr = value || []
                  onChange(checked ? arr.filter(x => x !== opt) : [...arr, opt])
                }}
                style={{ marginTop: 2, width: 'auto', flexShrink: 0 }} />
              {opt}
            </label>
          )
        })}
      </div>
    </div>
  )
  if (q.type === 'yesno') return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 6 }}>{q.label}</label>
      <div style={{ display: 'flex', gap: 10 }}>
        {['Yes', 'No'].map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 18px', borderRadius: 8, border: `0.5px solid ${value === opt ? '#006363' : 'rgba(0,0,0,0.2)'}`, background: value === opt ? '#e0f2f2' : '#fff', cursor: 'pointer', fontSize: 14, fontWeight: value === opt ? 500 : 400, color: value === opt ? '#004a4a' : '#333' }}>
            <input type="radio" name={q.id} value={opt} checked={value === opt} onChange={() => onChange(opt)} style={{ display: 'none' }} />
            {opt}
          </label>
        ))}
      </div>
    </div>
  )
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>{q.label}{q.required && <span style={{ color: '#c0392b', marginLeft: 3 }}>*</span>}</label>
      <input type={q.type === 'number' ? 'number' : q.type === 'date' ? 'date' : 'text'} min={q.type === 'number' ? q.min : undefined} value={value || ''} onChange={e => onChange(e.target.value)} onWheel={e => q.type === 'number' && e.currentTarget.blur()} style={s} />
    </div>
  )
}

export default function PublicForm() {
  const { token } = useParams()
  const [formConfig, setFormConfig] = useState(null)  // {audit_type, id}
  const [questions, setQuestions] = useState([])
  const [responses, setResponses] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadForm() }, [token])

  async function loadForm() {
    try {
      // Token-scoped read via RPC (no broad anon table access)
      const { data: cfg, error } = await supabase.rpc('get_public_form', { p_token: token })
      if (error) throw error
      if (!cfg || !cfg.audit_type) { setInvalid(true); setLoading(false); return }
      setFormConfig({ audit_type: cfg.audit_type, enquiry_link_id: cfg.enquiry_link_id, job_id: cfg.job_id, source: cfg.source })
      // ONE questionnaire per scheme. A link can carry a legacy or variant label for the same scheme
      // (e.g. "bizSAFE WSH RM Audit" is an alias of "bizSAFE Risk Management Audit"), so resolve it to the
      // canonical scope name BEFORE looking the template up. Matching the raw string would let an alias
      // quietly serve a different, stale template with different field ids — answers that then map to
      // nothing in the office edit screen, and in one case no application_type question at all.
      const canonicalType = resolveScope(cfg.audit_type)?.name || cfg.audit_type
      const { data: tmpl } = await supabase.from('questionnaire_templates')
        .select('*').eq('audit_type', canonicalType).maybeSingle()
      let qs = (tmpl?.questions?.length > 0) ? tmpl.questions : (DEFAULT_QUESTIONS[canonicalType] || DEFAULT_QUESTIONS[cfg.audit_type] || [])
      // Innate rule (Winnie, 22 Jul 2026): current level + cert expiry are ALWAYS required when shown,
      // even if a saved DB template still marks them optional. Renewal applicants kept skipping expiry,
      // which breaks the renewal-reminder lifecycle. showIf gating means New Applications are unaffected.
      qs = qs.map(q => ['expiry_date', 'current_level'].includes(q.id) ? { ...q, required: true } : q)
      setQuestions(qs)
      // Cold-outreach pre-fill (additive, best-effort). Returns null for office links, client
      // links and walk-ins -> form stays blank exactly as before. Never blocks the form loading;
      // seeded values are DEFAULTS only (spread first) so anything the person types always wins.
      try {
        const { data: pf } = await supabase.rpc('get_form_prefill', { p_token: token })
        if (pf && typeof pf === 'object') setResponses(r => ({ ...pf, ...r }))
      } catch (e) { console.error('prefill lookup failed (non-blocking):', e) }
      setLoading(false)
    } catch (e) {
      console.error('loadForm error:', e)
      setInvalid(true)
      setLoading(false)
    }
  }

  const [showErrors, setShowErrors] = useState(false)
  function set(id, val) { setResponses(r => ({ ...r, [id]: val })) }

  // A question shows only when its showIf passes. Supports a function (code defaults) OR a declarative
  // object from the DB template: { field, anyOf:[...] } or { field, equals }. e.g. expiry_date only on
  // Renewal/Progression.
  function shouldShow(q) {
    if (!q.showIf) return true
    if (typeof q.showIf === 'function') return q.showIf(responses)
    const v = responses[q.showIf.field]
    if (Array.isArray(q.showIf.anyOf)) return q.showIf.anyOf.includes(v)
    if ('equals' in q.showIf) return v === q.showIf.equals
    return true
  }
  const isFilled = q => { const v = responses[q.id]; return v != null && String(v).trim() !== '' && (!Array.isArray(v) || v.length > 0) }
  // Invalid = a shown required field left empty, OR a number below its min (e.g. 0 locations/employees).
  function fieldInvalid(q) {
    if (!shouldShow(q)) return false
    if (q.required && !isFilled(q)) return true
    if (q.type === 'number' && q.min != null && isFilled(q) && Number(responses[q.id]) < q.min) return true
    return false
  }
  function missingRequired() { return questions.filter(fieldInvalid) }

  async function submit() {
    const missing = missingRequired()
    if (missing.length) {
      setShowErrors(true)
      toast(`Please complete the ${missing.length} highlighted field${missing.length > 1 ? 's' : ''}`, 'warning')
      const el = document.getElementById(`fld-${missing[0].id}`)
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    setSubmitting(true)
    // Addresses go in as one line (also a pre-filled one that still had line breaks).
    const r = Object.fromEntries(Object.entries(responses).map(([k, v]) => [k, isAddressField({ id: k }) && typeof v === 'string' ? oneLine(v).trim() : v]))

    try {
      // Calculate man-days for bizSAFE (returning-client detection + quote creation
      // happen server-side in submit_enquiry_form).
      let mandays = null
      let calcDetails = null
      let complexity = null
      if (formConfig?.audit_type === AUDIT_TYPES.BIZSAFE) {
        // Single source of truth. Fixed scope resolves directly; only "Other Industries" uses complexity.
        let industry
        if (r.audit_scope === 'Other Industries') {
          const classified = classifyComplexity(r.ssic_code, r.business_nature)
          industry = classified.industry
          complexity = JSON.stringify({ ...classified, determined_by: 'System', determined_at: new Date().toISOString() })
        } else {
          industry = resolveCT17Industry(r.audit_scope)
        }
        const result = calcBizSAFEMandays(industry, r.num_employees, r.num_locations || 1)
        mandays = result.totalMandays
        calcDetails = result
      }

      const { data: res, error } = await supabase.rpc('submit_enquiry_form', {
        p_token: token,
        p_payload: {
          form_responses: r,
          mandays,
          calc_details: calcDetails,
          complexity_classification: complexity,
          initials: 'WT',
        },
      })
      if (error) throw error
      setSubmitted(true)
      // Take a cold prospect off the outreach list now that they've enquired. Token-gated and
      // best-effort: the quote is already created above, so this can NEVER affect the submission.
      try { await supabase.rpc('stop_outreach_for_token', { p_token: token }) } catch (e) { console.error('outreach stop failed (non-blocking):', e) }
      // Best-effort internal notification to the staff who sent the enquiry link. NEVER blocks submission.
      try {
        const company = res?.company_name || r.company_name || '—'
        // Client internal audit enquiry → the staff member who sent the link (never a group mailbox).
        if (isIA(res?.audit_type || formConfig?.audit_type)) {
          if (res?.created_by) {
            await supabase.functions.invoke('send-internal-email', { body: {
              user_id: res.created_by,
              // Same wording as the bizSAFE "Questionnaire Submitted" notice.
              subject: `Questionnaire Submitted – ${company}`,
              html: emailHtml({
                title: 'Questionnaire submitted', greeting: 'Dear {{staff_name}},',
                lines: ['A new internal audit questionnaire has been submitted. Please review the submission and prepare the quotation accordingly.'],
                details: [['Company', company], ['Audit type', 'Internal Audit'],
                  ['Standard(s)', [...(Array.isArray(r.ia_standards) ? r.ia_standards.filter(s => !/^other/i.test(s)) : []), r.ia_standards_other].filter(Boolean).join(', ') || '—'],
                  ['Quotation reference', res?.quote_ref || '—']],
                button: { label: 'Open Quotations →', url: `${window.location.origin}/quotations` },
              }),
            } })
          }
        } else {
        await supabase.functions.invoke('send-internal-email', {
          body: {
            // Office mailbox, always (Winnie's rule: never one person — leave-proof). Also covers
            // outreach-engine links, which have no creating staff member at all.
            to: 'bizSAFE@ccis.com.sg',
            subject: `Questionnaire Submitted – ${company}`,
            html: emailHtml({
              title: 'Questionnaire submitted',
              greeting: 'Dear Team,',
              lines: ['A new questionnaire has been submitted. Please review the submission and prepare the quotation accordingly.'],
              details: [['Company', company], ['Audit type', res?.audit_type || formConfig?.audit_type || '—'], ['Quotation reference', res?.quote_ref || '—']],
              button: { label: 'Open Quotations →', url: `${window.location.origin}/quotations` },
            }),
          },
        })
        }
      } catch (e) { console.error('enquiry notification failed (non-blocking):', e) }
    } catch (e) {
      console.error(e)
      failed("We couldn't submit your form", e.message, 'Please try again')
    }
    setSubmitting(false)
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3' }}>
      <div style={{ color: '#999', fontSize: 14 }}>Loading…</div>
    </div>
  )

  if (invalid) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 440, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>Invalid link</h2>
        <p style={{ color: '#666', fontSize: 14 }}>This form link is not valid or has expired. Please contact us for a new link.</p>
      </div>
    </div>
  )

  if (submitted) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 36, maxWidth: 480, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 36, marginBottom: 14 }}>✓</div>
        <h2 style={{ fontWeight: 500, marginBottom: 10, fontSize: 18 }}>Application received</h2>
        <p style={{ color: '#555', fontSize: 14, lineHeight: 1.7 }}>Thank you for your application. We will review the details and send you a quotation within 1–2 business days.</p>
        <p style={{ color: '#888', fontSize: 13, marginTop: 12 }}>CCIS Singapore Pte Ltd · query@ccis.com.sg</p>
      </div>
    </div>
  )

  const auditTypeLabels = {
    [AUDIT_TYPES.BIZSAFE]: 'bizSAFE WSH Risk Management Audit',
    [AUDIT_TYPES.SHMS]: 'SHMS Audit',
    [AUDIT_TYPES.ISO]: 'ISO Internal Audit',
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f7f6f3', padding: '40px 20px 60px' }}>
      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        <FirmHeader accredited={!isIA(formConfig?.audit_type)} />
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontWeight: 500, fontSize: 20, marginBottom: 6 }}>{auditTypeLabels[formConfig?.audit_type] || 'Audit Application'}</h1>
          <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>Please complete this application form. We will prepare a quotation based on your information. Fields marked <span style={{ color: '#c0392b' }}>*</span> are required.</p>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', border: '0.5px solid rgba(0,0,0,0.1)' }}>
          {questions.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#999', fontSize: 14 }}>
              This questionnaire has no questions configured. Please contact us directly.
            </div>
          ) : (
            questions
              .filter(q => shouldShow(q))
              .map(q => (
                <div key={q.id} id={`fld-${q.id}`}>
                  <Field q={q} value={responses[q.id]} onChange={v => set(q.id, v)}
                    invalid={showErrors && fieldInvalid(q)} />
                </div>
              ))
          )}
          <button
            onClick={submit}
            disabled={submitting}
            style={{ marginTop: 8, width: '100%', padding: '12px 20px', borderRadius: 10, border: 'none', background: '#006363', color: '#fff', fontWeight: 500, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {submitting ? 'Submitting…' : 'Submit application'}
          </button>
        </div>

      </div>
    </div>
  )
}